import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { cartLineKey, useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { EstimatedDaysBadge } from '../components/delivery/DeliveryDateBadges';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { buildLoginUrl, getReturnPathForAuthRedirect } from '../utils/authReturnUrl';
import CheckoutPaymentForm from '../components/checkout/CheckoutPaymentForm';
import type { CustomerProfile } from '../api/client';
import { ChevronDown, CircleHelp } from 'lucide-react';
import { useGeoAccess } from '../context/GeoAccessContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import { formatOrderReference } from '../utils/formatOrderReference';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const CHECKOUT_PRICING_SNAPSHOT_KEY = 'lewouhmart_checkout_pricing_snapshot_v1';

/** Remember legal acceptance on this browser per-user after a successful order hand-off to payment. */
const CHECKOUT_LEGAL_TERMS_STORAGE_PREFIX = 'lewouhmart_checkout_legal_terms_accepted_v2';

function checkoutTermsKeyForUser(userId: string | null | undefined): string | null {
  const id = String(userId ?? '').trim();
  if (!id) return null;
  return `${CHECKOUT_LEGAL_TERMS_STORAGE_PREFIX}:${id}`;
}

function readStoredCheckoutTermsAccepted(userId: string | null | undefined): boolean {
  const key = checkoutTermsKeyForUser(userId);
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function persistCheckoutTermsAccepted(userId: string | null | undefined): void {
  const key = checkoutTermsKeyForUser(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* ignore quota / private mode */
  }
}

type DeliveryAddress = {
  fullName: string;
  phone: string;
  street: string;
  street2?: string;
  city: string;
  state?: string;
  country: string;
  countryCode?: string;
  postalCode?: string;
  notes?: string;
};

function profileToDeliveryAddress(
  profile: NonNullable<CustomerProfile>,
): DeliveryAddress | null {
  const fullName = profile.fullName?.trim();
  const phone = profile.phone?.trim();
  const street = profile.street?.trim();
  const city = profile.city?.trim();
  const country = profile.country?.trim();
  if (!fullName || !phone || !street || !city || !country) return null;
  return {
    fullName,
    phone,
    street,
    street2: profile.street2?.trim() || undefined,
    city,
    state: profile.stateOrProvince?.trim() || undefined,
    country,
    countryCode: profile.countryCode?.trim() || undefined,
    postalCode: profile.postalCode?.trim() || undefined,
    notes: '',
  };
}

type Step = 'review' | 'pay';

type OrderPaymentSummary = {
  totalUsd: number;
  subtotalUsd: number;
  deliveryFeeUsd: number;
  taxUsd?: number;
  taxRatePct?: number;
  discountUsd: number;
  referralDiscountUsd?: number;
  retentionDiscountUsd?: number;
  retentionDiscountPercent?: number | null;
  /** Promotion code savings only (not checkout line % off). */
  promotionCodeDiscountUsd?: number;
  /** Sum of per-line marketing discounts after order is created (pay step). */
  pickDiscountUsd?: number;
  estimatedDeliveryDays?: number;
  isPreorderOrder?: boolean;
  preorderDepositUsd?: number;
  preorderRemainingUsd?: number;
  preorderCancellationFeeUsd?: number;
};

type MarketingPickDiscount = {
  enabled: boolean;
  minSubtotalUsd: number;
  nearGapUsd: number;
  percentOffOneLine: number;
  qualified: boolean;
  nearQualified: boolean;
  amountToQualifyUsd: number | null;
  cartSubtotalUsd: number;
  pickDiscountUsd: number;
};

type CarrierShippingOption = {
  provider: 'FEDEX' | 'UPS';
  serviceCode: string;
  serviceName: string;
  amountMinor: number;
  currency: string;
  transitDays?: number;
};

type Quote = {
  subtotalUsd: number;
  deliveryFeeUsd: number;
  taxUsd?: number;
  taxRatePct?: number;
  discountUsd: number;
  promotionCodeDiscountUsd: number;
  referralDiscountUsd: number;
  retentionDiscountUsd: number;
  retentionDiscountPercent: number | null;
  totalUsd: number;
  estimatedDeliveryDays?: number;
  deliverySpeed?: string;
  expressAllowed?: boolean | null;
  shippingMode?: 'IN_HOUSE' | 'CARRIER';
  carrierShippingOptions?: CarrierShippingOption[];
  carrierShippingRequired?: boolean;
  isPreorderOrder?: boolean;
  preorderDepositUsd?: number;
  preorderRemainingUsd?: number;
  marketingPickDiscount?: MarketingPickDiscount;
};

function parseQuoteResponse(data: Record<string, unknown>): Quote {
  const mk = data.marketingPickDiscount as Record<string, unknown> | undefined;
  return {
    subtotalUsd: Number(data.subtotalUsd),
    deliveryFeeUsd: Number(data.deliveryFeeUsd),
    taxUsd: Number(data.taxUsd ?? 0),
    taxRatePct:
      data.taxRatePct != null && data.taxRatePct !== ''
        ? Number(data.taxRatePct)
        : undefined,
    discountUsd: Number(data.discountUsd),
    promotionCodeDiscountUsd: Number(
      (data as { promotionCodeDiscountUsd?: number }).promotionCodeDiscountUsd ?? 0,
    ),
    referralDiscountUsd: Number(data.referralDiscountUsd ?? 0),
    retentionDiscountUsd: Number(data.retentionDiscountUsd ?? 0),
    retentionDiscountPercent:
      data.retentionDiscountPercent != null && data.retentionDiscountPercent !== ''
        ? Number(data.retentionDiscountPercent)
        : null,
    totalUsd: Number(data.totalUsd),
    estimatedDeliveryDays:
      data.estimatedDeliveryDays !== undefined ? Number(data.estimatedDeliveryDays) : undefined,
    deliverySpeed: data.deliverySpeed !== undefined ? String(data.deliverySpeed) : undefined,
    expressAllowed:
      data.expressAllowed === null
        ? null
        : data.expressAllowed !== undefined
          ? Boolean(data.expressAllowed)
          : undefined,
    shippingMode:
      data.shippingMode === 'CARRIER' || data.shippingMode === 'IN_HOUSE'
        ? data.shippingMode
        : undefined,
    carrierShippingOptions: Array.isArray(data.carrierShippingOptions)
      ? (data.carrierShippingOptions as CarrierShippingOption[])
      : undefined,
    carrierShippingRequired: Boolean(data.carrierShippingRequired),
    isPreorderOrder: Boolean(data.isPreorderOrder),
    preorderDepositUsd: Number(data.preorderDepositUsd ?? 0),
    preorderRemainingUsd: Number(data.preorderRemainingUsd ?? 0),
    marketingPickDiscount:
      mk && typeof mk === 'object'
        ? {
            enabled: !!mk.enabled,
            minSubtotalUsd: Number(mk.minSubtotalUsd),
            nearGapUsd: Number(mk.nearGapUsd),
            percentOffOneLine: Number(mk.percentOffOneLine),
            qualified: !!mk.qualified,
            nearQualified: !!mk.nearQualified,
            amountToQualifyUsd:
              mk.amountToQualifyUsd != null && mk.amountToQualifyUsd !== ''
                ? Number(mk.amountToQualifyUsd)
                : null,
            cartSubtotalUsd: Number(mk.cartSubtotalUsd),
            pickDiscountUsd: Number(mk.pickDiscountUsd ?? 0),
          }
        : undefined,
  };
}

type CheckoutProductLabel = { id: string; name: string };
type CheckoutStockShortage = {
  productId: string;
  name: string;
  requiredLbs: number;
  availableLbs: number;
  baseUnit: 'LB' | 'KG';
};

type CheckoutPreorderLimitViolation = {
  lineKey: string;
  productId: string;
  name: string;
  qtyLbs: number;
  maxLbs: number;
  baseUnit: 'LB' | 'KG';
};

type CheckoutProductSnapshot = {
  id?: string;
  name?: string;
  currentStockLbs?: number;
  baseUnit?: 'LB' | 'KG';
  isPreorderEnabled?: boolean;
  preorderDepositValue?: number | null;
  preorderDepositType?: 'PERCENT' | 'FIXED_USD' | null;
  preorderMaxQtyLbs?: number | null;
  salePrice?: number | null;
  pricePerUnit?: number | null;
};

const LB_PER_KG = 2.20462;
function toLbs(qty: number, unit?: string): number {
  const u = String(unit || '').toUpperCase();
  if (u === 'KG') return qty * LB_PER_KG;
  return qty;
}
function formatQtyForUnit(lbs: number, unit: 'LB' | 'KG'): string {
  if (unit === 'KG') return (lbs / LB_PER_KG).toFixed(2);
  return lbs.toFixed(2);
}

export default function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, auth, authorizedFetch } = useAuth();
  const { noOrders } = useGeoAccess();
  const { lines, clear } = useCart();
  const { currency, formatPrice, formatMajorInCurrency } = useCurrency();

  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';

  const stripePromise = useMemo(
    () => (STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null),
    [],
  );
  const stripeLocale = useMemo(() => {
    const raw = String(i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    const base = raw.split('-')[0];
    return base === 'fr' || base === 'es' || base === 'de' ? base : 'en';
  }, [i18n.language, i18n.resolvedLanguage]);

  const [profile, setProfile] = useState<CustomerProfile>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('review');
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [createdPreorderOrderId, setCreatedPreorderOrderId] = useState<string | null>(null);
  const [orderPaymentSummary, setOrderPaymentSummary] = useState<OrderPaymentSummary | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payNotice, setPayNotice] = useState<string | null>(null);

  const [promoInput, setPromoInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  /** Delivery address details collapsed by default to reduce visual noise */
  const [deliveryAddressExpanded, setDeliveryAddressExpanded] = useState(false);
  const [selectedCarrierShipping, setSelectedCarrierShipping] = useState<{
    provider: 'FEDEX' | 'UPS';
    serviceCode: string;
  } | null>(null);
  const userId = isAuthenticated ? (auth?.email ?? null) : null;
  /** Whether this browser has stored a prior acceptance for this user (used for label copy; user can still uncheck). */
  const [hasPreviouslyAcceptedTerms, setHasPreviouslyAcceptedTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pickDiscountProductId, setPickDiscountProductId] = useState<string | null>(null);
  const [productLabels, setProductLabels] = useState<Record<string, CheckoutProductLabel>>({});
  const [productsById, setProductsById] = useState<Record<string, CheckoutProductSnapshot>>({});
  const [packagePriceUsdById, setPackagePriceUsdById] = useState<Record<string, number>>({});
  const [unavailableIds, setUnavailableIds] = useState<string[]>([]);
  const [unavailablePackageIds, setUnavailablePackageIds] = useState<string[]>([]);
  const [pickProductIdCandidates, setPickProductIdCandidates] = useState<string[]>([]);
  const [stockShortages, setStockShortages] = useState<CheckoutStockShortage[]>([]);

  useEffect(() => {
    if (!isCustomer) return;
    setProfileLoading(true);
    void (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/auth/profile`);
        if (!res.ok) throw new Error('profile');
        const text = await res.text();
        const data = text ? (JSON.parse(text) as CustomerProfile) : null;
        setProfile(data);
      } catch {
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [isCustomer, authorizedFetch]);

  useEffect(() => {
    if (!isCustomer) {
      setHasPreviouslyAcceptedTerms(false);
      setTermsAccepted(false);
      return;
    }
    const prior = readStoredCheckoutTermsAccepted(userId);
    setHasPreviouslyAcceptedTerms(prior);
    setTermsAccepted(prior);
  }, [isCustomer, userId]);

  useEffect(() => {
    let cancelled = false;
    const productLineIds = lines
      .filter((l): l is Extract<(typeof lines)[number], { kind: 'product' }> => l.kind === 'product')
      .map((l) => l.productId);
    const packageLineIds = lines
      .filter((l): l is Extract<(typeof lines)[number], { kind: 'package' }> => l.kind === 'package')
      .map((l) => l.packageId);
    if (productLineIds.length === 0 && packageLineIds.length === 0) {
      setProductLabels({});
      setProductsById({});
      setPackagePriceUsdById({});
      setUnavailableIds([]);
      setUnavailablePackageIds([]);
      setPickProductIdCandidates([]);
      setStockShortages([]);
      return;
    }
    void (async () => {
      try {
        const labelIds = new Set<string>();
        const requiredLbsByProduct = new Map<string, number>();
        const unitByProduct = new Map<string, 'LB' | 'KG'>();
        for (const l of lines) {
          if (l.kind !== 'product') continue;
          requiredLbsByProduct.set(
            l.productId,
            (requiredLbsByProduct.get(l.productId) ?? 0) + toLbs(Number(l.qty || 0), l.unit),
          );
        }
        for (const id of productLineIds) labelIds.add(id);
        const pkgBad: string[] = [];
        const pickIds = new Set<string>();
        const packagePriceEntries: Array<[string, number]> = [];
        for (const id of productLineIds) pickIds.add(id);
        for (const pkgId of [...new Set(packageLineIds)]) {
          const packageUrl = new URL(
            applyContentLocaleToUrl(
              `${API_BASE}/catalog/packages/by-id/${encodeURIComponent(pkgId)}`,
              i18n.resolvedLanguage || i18n.language,
            ),
          );
          packageUrl.searchParams.set('currency', (currency || 'USD').toUpperCase());
          const res = await fetch(packageUrl.toString());
          if (!res.ok) {
            pkgBad.push(pkgId);
            continue;
          }
          const pkg = (await res.json()) as {
            priceUsd?: number | string;
            items?: Array<{ product?: { id: string; name?: string; baseUnit?: 'LB' | 'KG' }; qtyLbs?: number }>;
          };
          packagePriceEntries.push([pkgId, Number(pkg.priceUsd ?? 0)]);
          const lineQty = lines
            .filter((l): l is Extract<(typeof lines)[number], { kind: 'package' }> => l.kind === 'package' && l.packageId === pkgId)
            .reduce((sum, l) => sum + Number(l.qty || 0), 0);
          for (const row of pkg.items || []) {
            if (row.product?.id) {
              labelIds.add(row.product.id);
              pickIds.add(row.product.id);
              const need = Number(row.qtyLbs || 0) * lineQty;
              requiredLbsByProduct.set(
                row.product.id,
                (requiredLbsByProduct.get(row.product.id) ?? 0) + need,
              );
              if (row.product.baseUnit === 'KG') unitByProduct.set(row.product.id, 'KG');
            }
          }
        }
        const entries: Array<[string, CheckoutProductLabel]> = [];
        const productEntries: Array<[string, CheckoutProductSnapshot]> = [];
        const availableLbsByProduct = new Map<string, number>();
        const preorderEnabledByProduct = new Map<string, boolean>();
        for (const id of labelIds) {
          const productUrl = new URL(
            applyContentLocaleToUrl(
              `${API_BASE}/catalog/products/${encodeURIComponent(id)}`,
              i18n.resolvedLanguage || i18n.language,
            ),
          );
          productUrl.searchParams.set('currency', (currency || 'USD').toUpperCase());
          const res = await fetch(productUrl.toString());
          if (!res.ok) continue;
          const p = (await res.json()) as CheckoutProductSnapshot;
          if (p?.id && p?.name) {
            entries.push([id, { id: p.id, name: p.name }]);
            productEntries.push([id, p]);
            availableLbsByProduct.set(id, Number(p.currentStockLbs ?? 0));
            if (p.baseUnit === 'KG') unitByProduct.set(id, 'KG');
            preorderEnabledByProduct.set(id, Boolean(p.isPreorderEnabled));
          }
        }
        if (cancelled) return;
        const byId = Object.fromEntries(entries);
        const shortages: CheckoutStockShortage[] = [];
        for (const [pid, needLbs] of requiredLbsByProduct.entries()) {
          if (preorderEnabledByProduct.get(pid) === true) continue;
          const availableLbs = availableLbsByProduct.get(pid) ?? 0;
          if (availableLbs + 1e-9 < needLbs) {
            shortages.push({
              productId: pid,
              name: byId[pid]?.name || 'Product',
              requiredLbs: needLbs,
              availableLbs,
              baseUnit: unitByProduct.get(pid) || 'LB',
            });
          }
        }
        setProductLabels(byId);
        setProductsById(Object.fromEntries(productEntries));
        setPackagePriceUsdById(Object.fromEntries(packagePriceEntries));
        setUnavailableIds(productLineIds.filter((id) => !(id in byId)));
        setUnavailablePackageIds(pkgBad);
        setPickProductIdCandidates([...pickIds]);
        setStockShortages(shortages);
      } catch {
        if (!cancelled) {
          setProductLabels({});
          setProductsById({});
          setPackagePriceUsdById({});
          setUnavailableIds(productLineIds);
          setUnavailablePackageIds([...new Set(packageLineIds)]);
          setPickProductIdCandidates([]);
          setStockShortages([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, lines, i18n.language, i18n.resolvedLanguage]);

  const deliveryAddress = useMemo(() => {
    if (!profile) return null;
    return profileToDeliveryAddress(profile);
  }, [profile]);

  const DELIVERY_NOT_CONFIGURED_PREFIX = 'Delivery is not configured for country:';
  const isDeliveryNotConfigured = Boolean(
    quoteError?.includes(DELIVERY_NOT_CONFIGURED_PREFIX) ||
      error?.includes(DELIVERY_NOT_CONFIGURED_PREFIX),
  );
  const hasInsufficientStock = Boolean(
    quoteError?.toLowerCase().includes('insufficient stock') ||
      error?.toLowerCase().includes('insufficient stock'),
  );

  const preorderLimitViolations = useMemo((): CheckoutPreorderLimitViolation[] => {
    const out: CheckoutPreorderLimitViolation[] = [];
    for (const l of lines) {
      if (l.kind !== 'product') continue;
      const p = productsById[l.productId];
      if (!p?.isPreorderEnabled) continue;
      const maxLbs = Number(p.preorderMaxQtyLbs ?? 0);
      if (!Number.isFinite(maxLbs) || maxLbs <= 0) continue;
      const qtyLbs = toLbs(Number(l.qty || 0), l.unit);
      if (qtyLbs - maxLbs > 1e-6) {
        out.push({
          lineKey: cartLineKey(l),
          productId: l.productId,
          name: p.name || l.productId,
          qtyLbs,
          maxLbs,
          baseUnit: p.baseUnit === 'KG' ? 'KG' : l.unit,
        });
      }
    }
    return out;
  }, [lines, productsById]);

  const disabled = !isCustomer || lines.length === 0;
  const carrierShippingOptions = quote?.carrierShippingOptions ?? [];
  const canSubmit =
    Boolean(deliveryAddress) &&
    carrierShippingOptions.length > 0 &&
    !quote?.carrierShippingRequired &&
    selectedCarrierShipping != null;
  const reviewLocked =
    disabled ||
    submitting ||
    profileLoading ||
    quoteLoading ||
    hasInsufficientStock ||
    stockShortages.length > 0 ||
    preorderLimitViolations.length > 0 ||
    isDeliveryNotConfigured ||
    unavailableIds.length > 0 ||
    unavailablePackageIds.length > 0 ||
    noOrders;

  const cartKey = useMemo(
    () =>
      lines
        .map((l) =>
          l.kind === 'package'
            ? `k:${l.packageId}:${l.qty}`
            : `p:${l.productId}:${l.qty}:${l.unit}`,
        )
        .join('|'),
    [lines],
  );
  const [cartMerchSnapshotUsd, setCartMerchSnapshotUsd] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(CHECKOUT_PRICING_SNAPSHOT_KEY);
      if (!raw) {
        setCartMerchSnapshotUsd(null);
        return;
      }
      const parsed = JSON.parse(raw) as {
        cartKey?: string;
        merchandiseDueNowUsd?: number;
      };
      if (
        parsed?.cartKey === cartKey &&
        Number.isFinite(Number(parsed?.merchandiseDueNowUsd))
      ) {
        setCartMerchSnapshotUsd(Number(parsed.merchandiseDueNowUsd));
      } else {
        setCartMerchSnapshotUsd(null);
      }
    } catch {
      setCartMerchSnapshotUsd(null);
    }
  }, [cartKey]);

  const itemsPayload = useMemo(
    () =>
      lines.map((l) =>
        l.kind === 'package'
          ? { packageId: l.packageId, qty: l.qty }
          : {
              productId: l.productId,
              qty: l.qty,
              unit: l.unit,
              ...(l.packagingSelections?.length
                ? { packagingSelections: l.packagingSelections }
                : {}),
            },
      ),
    // cartKey is derived from lines; including lines would rememo every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartKey],
  );

  const buildOrderPayload = useCallback(
    (promoCode?: string | null) => {
      const mk = quote?.marketingPickDiscount;
      const pickId =
        mk?.enabled && mk?.qualified && pickDiscountProductId?.trim()
          ? pickDiscountProductId.trim()
          : undefined;
      return {
        currency,
        address: deliveryAddress!,
        items: itemsPayload,
        termsAccepted,
        ...(promoCode?.trim() ? { promoCode: promoCode.trim() } : {}),
        ...(pickId ? { pickDiscountProductId: pickId } : {}),
        ...(selectedCarrierShipping
          ? { carrierShipping: selectedCarrierShipping }
          : {}),
      };
    },
    [
      currency,
      deliveryAddress,
      itemsPayload,
      termsAccepted,
      quote?.marketingPickDiscount,
      pickDiscountProductId,
      selectedCarrierShipping,
    ],
  );

  const refreshQuote = useCallback(async () => {
    if (!isCustomer || !deliveryAddress || itemsPayload.length === 0) return;
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderPayload(appliedPromoCode)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'quote');
      }
      const raw = (await res.json()) as Record<string, unknown>;
      setQuote(parseQuoteResponse(raw));
    } catch (e) {
      setQuote(null);
      setQuoteError(e instanceof Error ? e.message : 'quote');
    } finally {
      setQuoteLoading(false);
    }
  }, [
    appliedPromoCode,
    authorizedFetch,
    buildOrderPayload,
    deliveryAddress,
    isCustomer,
    itemsPayload.length,
  ]);

  useEffect(() => {
    const mk = quote?.marketingPickDiscount;
    if (!mk?.enabled) {
      setPickDiscountProductId(null);
      return;
    }
    if (!mk.qualified) {
      setPickDiscountProductId(null);
      return;
    }
    const ids = pickProductIdCandidates.map(String);
    setPickDiscountProductId((prev) => {
      if (prev && ids.includes(prev)) return prev;
      return ids[0] ?? null;
    });
  }, [quote?.marketingPickDiscount, cartKey, pickProductIdCandidates]);

  useEffect(() => {
    if (step !== 'review' || !deliveryAddress || itemsPayload.length === 0) return;
    void refreshQuote();
    // appliedPromoCode + cartKey drive when to re-fetch server totals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, deliveryAddress, cartKey, appliedPromoCode, pickDiscountProductId, selectedCarrierShipping]);

  useEffect(() => {
    const options = quote?.carrierShippingOptions || [];
    if (options.length === 0) {
      setSelectedCarrierShipping(null);
      return;
    }
    setSelectedCarrierShipping((prev) => {
      if (
        prev &&
        options.some(
          (o) => o.provider === prev.provider && o.serviceCode === prev.serviceCode,
        )
      ) {
        return prev;
      }
      const first = options[0];
      return { provider: first.provider, serviceCode: first.serviceCode };
    });
  }, [quote?.carrierShippingOptions]);

  const applyPromo = async () => {
    if (!promoInput.trim()) {
      setAppliedPromoCode(null);
      setQuoteError(null);
      await refreshQuote();
      return;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderPayload(promoInput)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('storefront.order_place_error'));
      }
      const raw = (await res.json()) as Record<string, unknown>;
      setAppliedPromoCode(promoInput.trim());
      setQuote(parseQuoteResponse(raw));
    } catch (e) {
      setQuoteError(e instanceof Error ? e.message : t('storefront.order_place_error'));
    } finally {
      setQuoteLoading(false);
    }
  };

  const clearPromo = async () => {
    setPromoInput('');
    setAppliedPromoCode(null);
    setQuoteError(null);
    setQuoteLoading(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderPayload(null)),
      });
      if (res.ok) {
        const raw = (await res.json()) as Record<string, unknown>;
        setQuote(parseQuoteResponse(raw));
      }
    } finally {
      setQuoteLoading(false);
    }
  };

  const startPaymentStep = async (
    orderId: string,
    purpose: 'FULL' | 'DEPOSIT' | 'BALANCE' = 'FULL',
  ) => {
    setPayNotice(null);
    setClientSecret(null);
    try {
      const cfgRes = await fetch(`${API_BASE}/payments/config`);
      const cfg = (await cfgRes.json()) as { paymentsEnabled?: boolean };
      if (!cfg.paymentsEnabled) {
        setPayNotice(t('storefront.checkout_stripe_server_off'));
        setStep('pay');
        return;
      }
      if (!stripePromise) {
        setPayNotice(t('storefront.checkout_stripe_no_pk'));
        setStep('pay');
        return;
      }
      const res = await authorizedFetch(`${API_BASE}/payments/intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, purpose }),
      });
      if (res.status === 503) {
        setPayNotice(t('storefront.checkout_stripe_server_off'));
        setStep('pay');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('storefront.checkout_pay_prepare_error'));
      }
      const data = (await res.json()) as { clientSecret: string | null };
      if (!data.clientSecret) {
        throw new Error(t('storefront.checkout_pay_prepare_error'));
      }
      setClientSecret(data.clientSecret);
      setStep('pay');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('storefront.checkout_pay_prepare_error'));
      setStep('pay');
    }
  };

  const handleCreateOrder = async () => {
    if (!isCustomer) {
      navigate(buildLoginUrl(getReturnPathForAuthRedirect()));
      return;
    }
    if (lines.length === 0) return;
    if (
      stockShortages.length > 0 ||
      preorderLimitViolations.length > 0 ||
      unavailableIds.length > 0 ||
      unavailablePackageIds.length > 0 ||
      noOrders ||
      hasInsufficientStock ||
      isDeliveryNotConfigured
    ) {
      return;
    }
    if (!deliveryAddress) {
      setError(t('storefront.checkout_profile_incomplete'));
      return;
    }
    if (!termsAccepted) {
      setError(t('storefront.checkout_terms_required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/sales/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderPayload(appliedPromoCode)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('storefront.order_place_error'));
      }
      const order = (await res.json()) as {
        id: string;
        totalUsd: number | string;
        subtotalUsd: number | string;
        deliveryFeeUsd: number | string;
        taxUsd?: number | string;
        taxRatePct?: number | string;
        discountUsd?: number | string;
        referralDiscountUsd?: number | string;
        retentionDiscountUsd?: number | string;
        retentionDiscountPercent?: number | string | null;
        promotionCodeDiscountUsd?: number | string;
        items?: Array<{ lineDiscountUsd?: number | string | null }>;
        estimatedDeliveryDays?: number | string;
        isPreorderOrder?: boolean;
        preorderDepositUsd?: number | string;
        preorderRemainingUsd?: number | string;
        preorderCancellationFeeUsd?: number | string;
        createdOrders?: Array<{
          fulfillmentType: 'IN_STOCK' | 'PREORDER';
          order: any;
        }>;
      };
      const primaryOrder =
        Array.isArray(order.createdOrders) && order.createdOrders.length > 0
          ? (order.createdOrders.find((o) => o.fulfillmentType === 'IN_STOCK') ||
            order.createdOrders[0])?.order
          : order;
      const preorderOrder =
        Array.isArray(order.createdOrders) && order.createdOrders.length > 0
          ? order.createdOrders.find((o) => o.fulfillmentType === 'PREORDER')?.order
          : null;
      const pickFromLines = Array.isArray(primaryOrder.items)
        ? primaryOrder.items.reduce(
            (
              s: number,
              it: { lineDiscountUsd?: number | string | null },
            ) => s + Number(it.lineDiscountUsd ?? 0),
            0,
          )
        : 0;
      setCreatedOrderId(primaryOrder.id);
      setCreatedPreorderOrderId(preorderOrder?.id ?? null);
      setOrderPaymentSummary({
        totalUsd: Number(primaryOrder.totalUsd),
        subtotalUsd: Number(primaryOrder.subtotalUsd),
        deliveryFeeUsd: Number(primaryOrder.deliveryFeeUsd),
        taxUsd: Number(primaryOrder.taxUsd ?? 0),
        taxRatePct:
          primaryOrder.taxRatePct != null && primaryOrder.taxRatePct !== ''
            ? Number(primaryOrder.taxRatePct)
            : undefined,
        discountUsd: Number(primaryOrder.discountUsd ?? 0),
        referralDiscountUsd: Number(primaryOrder.referralDiscountUsd ?? 0),
        retentionDiscountUsd: Number(primaryOrder.retentionDiscountUsd ?? 0),
        retentionDiscountPercent:
          primaryOrder.retentionDiscountPercent != null && primaryOrder.retentionDiscountPercent !== ''
            ? Number(primaryOrder.retentionDiscountPercent)
            : null,
        promotionCodeDiscountUsd: Number(primaryOrder.promotionCodeDiscountUsd ?? 0),
        pickDiscountUsd: pickFromLines,
        estimatedDeliveryDays:
          primaryOrder.estimatedDeliveryDays !== undefined
            ? Number(primaryOrder.estimatedDeliveryDays)
            : undefined,
        isPreorderOrder: Boolean(primaryOrder.isPreorderOrder),
        preorderDepositUsd: Number(primaryOrder.preorderDepositUsd ?? 0),
        preorderRemainingUsd: Number(primaryOrder.preorderRemainingUsd ?? 0),
        preorderCancellationFeeUsd: Number(primaryOrder.preorderCancellationFeeUsd ?? 0),
      });
      persistCheckoutTermsAccepted(userId);
      await startPaymentStep(
        primaryOrder.id,
        primaryOrder.isPreorderOrder ? 'DEPOSIT' : 'FULL',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('storefront.order_place_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaySuccess = async () => {
    if (createdOrderId) {
      try {
        await authorizedFetch(`${API_BASE}/payments/orders/${createdOrderId}/sync`, {
          method: 'POST',
        });
      } catch {
        // Webhook may still mark the order paid; orders list could be one refresh behind
      }
    }
    clear();
    navigate('/account/orders', { replace: true, state: { orderId: createdOrderId } });
  };

  const displaySubtotalMajor = useMemo(() => {
    let subtotal = 0;
    for (const line of lines) {
      if (line.kind === 'product') {
        const product = productsById[line.productId];
        if (!product) continue;
        const unitUsd = Number(product.salePrice ?? product.pricePerUnit ?? 0);
        const qty = Number(line.qty || 0);
        if (!product.isPreorderEnabled) {
          subtotal += Number((unitUsd * qty).toFixed(2));
          continue;
        }
        const rawDeposit = Number(product.preorderDepositValue ?? 20);
        const depositType = String(product.preorderDepositType ?? 'PERCENT');
        if (depositType === 'FIXED_USD') {
          subtotal += Number((Math.max(0, rawDeposit) * qty).toFixed(2));
        } else {
          subtotal += Number((unitUsd * qty * (Math.max(0, rawDeposit) / 100)).toFixed(2));
        }
      } else {
        const unitUsd = Number(packagePriceUsdById[line.packageId] ?? 0);
        subtotal += Number(
          (
            unitUsd * Math.max(1, Math.floor(Number(line.qty || 0)))
          ).toFixed(2),
        );
      }
    }
    return Number(subtotal.toFixed(2));
  }, [lines, packagePriceUsdById, productsById]);

  const displayCurrencyCode = 'USD';
  const reviewMerchandiseSubtotalUsd = cartMerchSnapshotUsd ?? displaySubtotalMajor;

  const SummaryCard = ({
    summary,
    showPromoHint,
    variant = 'hero',
    displaySubtotal,
    displayCurrency,
  }: {
    summary: Quote | OrderPaymentSummary;
    showPromoHint?: boolean;
    /** `hero` = total + breakdown (review). `breakdown` = line items only (pay step, total shown on the left). */
    variant?: 'hero' | 'breakdown';
    displaySubtotal?: number;
    displayCurrency?: string;
  }) => {
    const displayCode = (displayCurrency || 'USD').toUpperCase();
    const retentionUsd =
      'retentionDiscountUsd' in summary && summary.retentionDiscountUsd != null
        ? Number(summary.retentionDiscountUsd)
        : 0;
    const retentionPct =
      'retentionDiscountPercent' in summary ? summary.retentionDiscountPercent : null;
    const referralUsd =
      'referralDiscountUsd' in summary && summary.referralDiscountUsd != null
        ? Number(summary.referralDiscountUsd)
        : 0;
    const promoCodeUsd =
      'promotionCodeDiscountUsd' in summary && summary.promotionCodeDiscountUsd != null
        ? Number(summary.promotionCodeDiscountUsd)
        : 0;
    const pickUsd =
      'marketingPickDiscount' in summary &&
      summary.marketingPickDiscount &&
      summary.marketingPickDiscount.pickDiscountUsd > 1e-9
        ? summary.marketingPickDiscount.pickDiscountUsd
        : 'pickDiscountUsd' in summary && summary.pickDiscountUsd != null
          ? Number(summary.pickDiscountUsd)
          : 0;
    const showRetention = retentionUsd > 1e-9;
    const showReferral = referralUsd > 1e-9;
    const showPromoCode = promoCodeUsd > 1e-9;
    const showPick = pickUsd > 1e-9;
    const mkPick =
      'marketingPickDiscount' in summary && summary.marketingPickDiscount
        ? summary.marketingPickDiscount
        : null;
    const taxUsd = 'taxUsd' in summary && summary.taxUsd != null ? Number(summary.taxUsd) : 0;
    const taxRatePct =
      'taxRatePct' in summary && summary.taxRatePct != null
        ? Number(summary.taxRatePct)
        : null;
    const subtotalMajor = Number.isFinite(Number(displaySubtotal))
      ? Number(displaySubtotal)
      : Number(summary.subtotalUsd ?? 0);
    const deliveryMajor = Number(summary.deliveryFeeUsd ?? 0);
    const taxMajor = taxUsd;
    const retentionMajor = retentionUsd;
    const referralMajor = referralUsd;
    const promoCodeMajor = promoCodeUsd;
    const pickMajor = pickUsd;
    const totalMajor = Number(summary.totalUsd ?? 0);
    const displaySubtotalLabel = formatMajorInCurrency(subtotalMajor, displayCode);
    const displayDelivery = formatMajorInCurrency(deliveryMajor, displayCode);
    const displayTax = formatMajorInCurrency(taxMajor, displayCode);
    const displayRetention = formatMajorInCurrency(retentionMajor, displayCode);
    const displayReferral = formatMajorInCurrency(referralMajor, displayCode);
    const displayPromoCode = formatMajorInCurrency(promoCodeMajor, displayCode);
    const displayPick = formatMajorInCurrency(pickMajor, displayCode);
    const preorderDueNowUsd =
      'isPreorderOrder' in summary &&
      summary.isPreorderOrder &&
      Number(summary.preorderDepositUsd ?? 0) > 0
        ? Number(summary.preorderDepositUsd ?? 0)
        : null;
    const preorderRemainingUsd =
      'isPreorderOrder' in summary &&
      summary.isPreorderOrder &&
      Number(summary.preorderRemainingUsd ?? 0) > 0
        ? Number(summary.preorderRemainingUsd ?? 0)
        : null;
    const preorderDueNowMajor =
      preorderDueNowUsd != null ? preorderDueNowUsd : null;
    const preorderRemainingMajor = preorderRemainingUsd;
    const displayHeadlineTotal = formatMajorInCurrency(
      preorderDueNowMajor != null ? preorderDueNowMajor : totalMajor,
      displayCode,
    );

    const breakdown = (
      <>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">{t('storefront.subtotal')}</dt>
          <dd className="font-medium tabular-nums">
            {displaySubtotalLabel}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-500">
            {t('storefront.checkout_delivery_fee')}
            {preorderDueNowUsd != null ? (
              <span className="ml-1 text-[11px] text-slate-400">
                {t('storefront.checkout_delivery_fee_later_hint', {
                  defaultValue: '(included in remaining balance)',
                })}
              </span>
            ) : null}
          </dt>
          <dd className="font-medium tabular-nums">
            {displayDelivery}
          </dd>
        </div>
        {taxUsd > 1e-9 ? (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">
              {t('storefront.checkout_tax', {
                defaultValue: 'Tax',
              })}
              {taxRatePct != null && taxRatePct > 0 ? (
                <span className="ml-1 text-[11px] text-slate-400">({taxRatePct.toFixed(1)}%)</span>
              ) : null}
            </dt>
            <dd className="font-medium tabular-nums">{displayTax}</dd>
          </div>
        ) : null}
        {showRetention ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">
                {t('storefront.checkout_retention_discount', {
                  defaultValue: 'Stay offer (account retention)',
                })}
              </dt>
              <dd className="font-medium tabular-nums text-emerald-800">
                −{displayRetention}
              </dd>
            </div>
            {retentionPct != null && retentionPct > 1e-9 ? (
              <p className="text-[11px] leading-snug text-emerald-800/90">
                {t('storefront.checkout_retention_percent_line', {
                  defaultValue: '{{percent}}% off merchandise subtotal (one time)',
                  percent: retentionPct % 1 === 0 ? String(retentionPct) : retentionPct.toFixed(1),
                })}
              </p>
            ) : null}
          </div>
        ) : null}
        {showReferral ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">
                {t('storefront.checkout_discount_referral', {
                  defaultValue: 'Referral welcome',
                })}
              </dt>
              <dd className="font-medium tabular-nums text-emerald-800">
                −{displayReferral}
              </dd>
            </div>
            <p className="text-[11px] leading-snug text-emerald-800/90">
              {t('storefront.checkout_referral_welcome_subline', {
                amount: displayReferral,
              })}
            </p>
          </div>
        ) : null}
        {showPromoCode ? (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">
              {t('storefront.checkout_discount_promo_code', {
                defaultValue: 'Promotion code',
              })}
            </dt>
            <dd className="font-medium tabular-nums text-emerald-800">
              −{displayPromoCode}
            </dd>
          </div>
        ) : null}
        {showPick ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">
                {t('storefront.checkout_discount_checkout_line', {
                  defaultValue: 'Checkout line offer',
                })}
              </dt>
              <dd className="font-medium tabular-nums text-emerald-800">
                −{displayPick}
              </dd>
            </div>
            {mkPick && mkPick.percentOffOneLine > 1e-9 ? (
              <p className="text-[11px] leading-snug text-emerald-800/90">
                {t('storefront.checkout_pick_discount_subline', {
                  percent: mkPick.percentOffOneLine,
                  amount: displayPick,
                })}
              </p>
            ) : null}
          </div>
        ) : null}
        {'estimatedDeliveryDays' in summary &&
        summary.estimatedDeliveryDays != null &&
        Number.isFinite(Number(summary.estimatedDeliveryDays)) ? (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">{t('storefront.delivery_eta')}</dt>
            <dd className="flex justify-end">
              <EstimatedDaysBadge className="text-sm">
                ~
                {t('account.eta_days', {
                  days: Math.round(Number(summary.estimatedDeliveryDays)),
                })}
              </EstimatedDaysBadge>
            </dd>
          </div>
        ) : null}
        {preorderDueNowUsd != null ? (
          <div className="flex justify-between gap-4 border-t border-slate-200 pt-2">
            <dt className="font-medium text-slate-700">
              {t('storefront.checkout_due_now', { defaultValue: 'Due now' })}
            </dt>
            <dd className="font-semibold tabular-nums text-slate-900">
              {formatMajorInCurrency(
                preorderDueNowMajor ?? preorderDueNowUsd,
                displayCode,
              )}
            </dd>
          </div>
        ) : null}
        {preorderRemainingUsd != null ? (
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">
              {t('storefront.checkout_remaining_later', {
                defaultValue: 'Remaining at release',
              })}
            </dt>
            <dd className="font-medium tabular-nums">
              {formatMajorInCurrency(
                preorderRemainingMajor ?? preorderRemainingUsd,
                displayCode,
              )}
            </dd>
          </div>
        ) : null}
      </>
    );

    if (variant === 'breakdown') {
      return (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('storefront.checkout_breakdown_title')}
          </p>
          <div className="mt-3 space-y-2 text-sm text-slate-700">{breakdown}</div>
        </div>
      );
    }

    return (
      <div className="rounded-lg border-2 border-[#00b853] bg-gradient-to-b from-emerald-50/90 to-white px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900/80">
          {t('storefront.checkout_total_due')}
        </p>
        <p className="mt-0.5 text-[2rem] font-bold tracking-tight text-slate-900">
          {displayHeadlineTotal}
        </p>
        <dl className="mt-3 space-y-1.5 border-t border-emerald-200/60 pt-3 text-sm text-slate-700">
          {breakdown}
        </dl>
        {showPromoHint ? (
          <p className="mt-2 text-xs text-slate-500">{t('storefront.checkout_promo_hint')}</p>
        ) : null}
      </div>
    );
  };

  const reviewSubtotalMajor = quote ? Number(displaySubtotalMajor) : null;
  const reviewDeliveryMajor = quote ? Number(quote.deliveryFeeUsd ?? 0) : null;
  const reviewTaxMajor = quote ? Number(quote.taxUsd ?? 0) : null;
  const reviewDiscountMajor = quote
    ? (
      Number(quote.retentionDiscountUsd ?? 0)
      + Number(quote.referralDiscountUsd ?? 0)
      + Number(quote.promotionCodeDiscountUsd ?? 0)
      + (quote.marketingPickDiscount?.pickDiscountUsd
        ? Number(quote.marketingPickDiscount.pickDiscountUsd)
        : 0)
    )
    : null;
  const reviewTotalMajor =
    reviewSubtotalMajor == null || reviewDeliveryMajor == null || reviewTaxMajor == null
      ? null
      : Number(
        (
          reviewSubtotalMajor +
          reviewDeliveryMajor +
          reviewTaxMajor -
          (reviewDiscountMajor ?? 0)
        ).toFixed(2),
      );
  const reviewDueNowMajor =
    quote?.isPreorderOrder && Number(quote.preorderDepositUsd ?? 0) > 0
      ? Number(quote.preorderDepositUsd ?? 0)
      : reviewTotalMajor;
  const reviewSubtotalDisplay = formatMajorInCurrency(
    reviewSubtotalMajor ?? 0,
    displayCurrencyCode,
  );
  const reviewDeliveryDisplay = formatMajorInCurrency(
    reviewDeliveryMajor ?? 0,
    displayCurrencyCode,
  );
  const reviewDiscountDisplay = formatMajorInCurrency(
    reviewDiscountMajor ?? 0,
    displayCurrencyCode,
  );
  const reviewTotalDisplay = formatMajorInCurrency(
    reviewDueNowMajor ?? 0,
    displayCurrencyCode,
  );
  const payStepDisplayTotalMajor = useMemo(() => {
    if (!orderPaymentSummary) return null;
    return Number(orderPaymentSummary.totalUsd ?? 0);
  }, [orderPaymentSummary]);

  return (
    <StorefrontLayout mainClassName="bg-transparent" contentContained>
      <div className="mx-auto w-full max-w-5xl pb-44 sm:pb-0">
        <h1 className="mb-0.5 text-xl font-semibold text-slate-900 sm:mb-1 sm:text-2xl">
          {t('storefront.checkout')}
        </h1>
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-500 sm:mb-4 sm:text-xs">
          <span className={step === 'review' ? 'text-[#00b853]' : ''}>
            1. {t('storefront.checkout_step_review')}
          </span>
          <span>→</span>
          <span className={step === 'pay' ? 'text-[#00b853]' : ''}>
            2. {t('storefront.checkout_step_pay')}
          </span>
        </div>

        {!isAuthenticated && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {t('storefront.login_required')}
            <button
              type="button"
              className="ml-3 font-semibold underline"
              onClick={() => navigate(buildLoginUrl(getReturnPathForAuthRedirect()))}
            >
              {t('auth.tab_sign_in')}
            </button>
          </div>
        )}

        {isAuthenticated && !isCustomer && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {t('storefront.checkout_customers_only')}
          </div>
        )}

        {noOrders && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {t('storefront.checkout_region_blocked', {
              defaultValue: 'Ordering is not available in your region.',
            })}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-none border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {(unavailableIds.length > 0 || unavailablePackageIds.length > 0) && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            {t(
              'storefront.checkout_unavailable_warning',
              'Some items in your cart are no longer available. Please go back to your cart and remove them to continue.',
            )}
          </div>
        )}
        {hasInsufficientStock && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <p>
              {t(
                'storefront.checkout_insufficient_stock_block',
                'You cannot continue checkout because one or more items do not have enough stock.',
              )}
            </p>
            <Link to="/cart" className="mt-2 inline-block font-semibold underline">
              {t('storefront.back_to_cart', { defaultValue: 'Back to cart' })}
            </Link>
          </div>
        )}
        {stockShortages.length > 0 && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <p className="font-semibold">
              {t(
                'storefront.checkout_stock_remaining_title',
                'Insufficient stock for the following items:',
              )}
            </p>
            <ul className="mt-2 space-y-1">
              {stockShortages.map((s) => (
                <li key={s.productId}>
                  {s.name}: {t('storefront.checkout_stock_remaining_line', {
                    defaultValue: '{{available}} available / {{required}} required ({{unit}})',
                    available: formatQtyForUnit(s.availableLbs, s.baseUnit),
                    required: formatQtyForUnit(s.requiredLbs, s.baseUnit),
                    unit: s.baseUnit,
                  })}
                </li>
              ))}
            </ul>
            <Link to="/cart" className="mt-2 inline-block font-semibold underline">
              {t('storefront.back_to_cart', { defaultValue: 'Back to cart' })}
            </Link>
          </div>
        )}
        {preorderLimitViolations.length > 0 && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            <p className="font-semibold">
              {t(
                'storefront.checkout_preorder_limit_title',
                'Pre-order quantity limits exceeded:',
              )}
            </p>
            <ul className="mt-2 space-y-1">
              {preorderLimitViolations.map((v) => (
                <li key={v.lineKey}>
                  {v.name}:{' '}
                  {t('storefront.cart_preorder_limit_exceeded', {
                    defaultValue:
                      'This pre-order is limited to {{max}} lbs total; your line is {{current}} lbs.',
                    max: v.maxLbs.toFixed(1),
                    current: v.qtyLbs.toFixed(1),
                  })}
                </li>
              ))}
            </ul>
            <Link to="/cart" className="mt-2 inline-block font-semibold underline">
              {t('storefront.back_to_cart', { defaultValue: 'Back to cart' })}
            </Link>
          </div>
        )}

        {isCustomer && step === 'review' && (
          <div className="grid gap-4 lg:grid-cols-[1fr_minmax(280px,380px)] lg:items-start lg:gap-6">
            <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t('storefront.delivery_address')}
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    {t('storefront.delivery_from_profile')}
                  </p>
                </div>
                {deliveryAddress && !profileLoading ? (
                  <button
                    type="button"
                    className="shrink-0 rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                    onClick={() => setDeliveryAddressExpanded((open) => !open)}
                    aria-expanded={deliveryAddressExpanded}
                    aria-controls="checkout-delivery-address-details"
                    title={
                      deliveryAddressExpanded
                        ? t('storefront.checkout_address_toggle_collapse')
                        : t('storefront.checkout_address_toggle_expand')
                    }
                    disabled={reviewLocked}
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${deliveryAddressExpanded ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                    <span className="sr-only">
                      {deliveryAddressExpanded
                        ? t('storefront.checkout_address_toggle_collapse')
                        : t('storefront.checkout_address_toggle_expand')}
                    </span>
                  </button>
                ) : null}
              </div>

              {profileLoading ? (
                <p className="mt-4 text-sm text-slate-600">
                  {t('common.loading')}
                </p>
              ) : !deliveryAddress ? (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900">
                  <p>
                    {t('storefront.checkout_complete_profile_prompt')}
                  </p>
                  <Link
                    to="/account"
                    className="mt-3 inline-block text-sm font-semibold text-[#00b853] underline"
                  >
                    {t('storefront.checkout_go_account')}
                  </Link>
                </div>
              ) : (
                <>
                  {!deliveryAddressExpanded ? (
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                      <span className="font-medium text-slate-900">{deliveryAddress.fullName}</span>
                      <span className="text-slate-400"> · </span>
                      <span>
                        {deliveryAddress.city}, {deliveryAddress.country}
                        {deliveryAddress.postalCode ? ` ${deliveryAddress.postalCode}` : ''}
                      </span>
                    </p>
                  ) : null}
                  {deliveryAddressExpanded ? (
                    <dl
                      id="checkout-delivery-address-details"
                      className="mt-4 space-y-2 text-sm text-slate-700"
                    >
                      <div>
                        <dt className="text-xs font-medium text-slate-500">
                          {t('account.field_full_name')}
                        </dt>
                        <dd>{deliveryAddress.fullName}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-slate-500">
                          {t('account.field_phone')}
                        </dt>
                        <dd>{deliveryAddress.phone}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-slate-500">
                          {t('account.field_street')}
                        </dt>
                        <dd>{deliveryAddress.street}</dd>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <dt className="text-xs font-medium text-slate-500">
                            {t('account.field_city')}
                          </dt>
                          <dd>{deliveryAddress.city}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-slate-500">
                            {t('account.field_country')}
                          </dt>
                          <dd>{deliveryAddress.country}</dd>
                        </div>
                      </div>
                      {deliveryAddress.postalCode ? (
                        <div>
                          <dt className="text-xs font-medium text-slate-500">
                            {t('account.field_postal_code')}
                          </dt>
                          <dd>{deliveryAddress.postalCode}</dd>
                        </div>
                      ) : null}
                      <div className="pt-2">
                        <Link
                          to="/account"
                          className="text-xs font-semibold text-[#00b853] hover:underline"
                        >
                          {t('storefront.checkout_edit_address')}
                        </Link>
                      </div>
                    </dl>
                  ) : null}
                </>
              )}

              <div className="mt-4 hidden rounded-xl border border-slate-100 bg-slate-50/80 p-3 sm:block">
                <p className="text-xs font-medium text-slate-600">
                  {t('storefront.checkout_security_note')}
                </p>
              </div>

              {deliveryAddress ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 sm:mt-4 sm:p-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    {t('storefront.carrier_shipping_title', {
                      defaultValue: 'Carrier shipping',
                    })}
                  </h3>
                  <p className="mt-1 text-xs text-slate-600">
                    {t('storefront.carrier_shipping_subtitle', {
                      defaultValue:
                        'Live rates from FedEx and UPS based on your cart weight and delivery address.',
                    })}
                  </p>
                  {quoteLoading || !quote ? (
                    <p className="mt-3 text-sm text-slate-600">{t('common.loading')}</p>
                  ) : carrierShippingOptions.length === 0 ? (
                    <p className="mt-3 text-sm text-amber-800">
                      {t('storefront.carrier_shipping_unavailable', {
                        defaultValue:
                          'No carrier shipping options are available for this address. Please contact support.',
                      })}
                    </p>
                  ) : (
                    <>
                      <div className="mt-2.5 space-y-2.5 sm:mt-3 sm:space-y-3">
                        {carrierShippingOptions.map((option) => {
                          const selected =
                            selectedCarrierShipping?.provider === option.provider &&
                            selectedCarrierShipping?.serviceCode === option.serviceCode;
                          const priceMajor = option.amountMinor / 100;
                          const providerLabel =
                            option.provider === 'FEDEX' ? 'FedEx' : 'UPS';
                          return (
                            <label
                              key={`${option.provider}:${option.serviceCode}`}
                              className={[
                                'flex cursor-pointer gap-2.5 rounded-lg border p-2.5 sm:gap-3 sm:p-3',
                                selected
                                  ? 'border-[#00b853] bg-emerald-50/50'
                                  : 'border-slate-200',
                              ].join(' ')}
                            >
                              <input
                                type="radio"
                                name="carrierShipping"
                                className="mt-1"
                                checked={selected}
                                onChange={() =>
                                  setSelectedCarrierShipping({
                                    provider: option.provider,
                                    serviceCode: option.serviceCode,
                                  })
                                }
                                disabled={reviewLocked}
                              />
                              <span className="min-w-0 flex-1">
                                <span className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="block text-sm font-semibold text-slate-900">
                                    {providerLabel} · {option.serviceName}
                                  </span>
                                  <span className="text-sm font-semibold text-slate-900">
                                    {formatPrice(priceMajor)}
                                  </span>
                                </span>
                                {option.transitDays != null ? (
                                  <span className="mt-0.5 block text-xs text-slate-600">
                                    {t('storefront.carrier_shipping_transit', {
                                      defaultValue: 'Typical transit ~{{days}} business day(s)',
                                      days: option.transitDays,
                                    })}
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {quote.carrierShippingRequired ? (
                        <p className="mt-2 text-xs text-amber-700">
                          {t('storefront.carrier_shipping_select_required', {
                            defaultValue: 'Select a shipping option to continue.',
                          })}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}

              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 lg:hidden">
                <label className="block text-xs font-medium text-slate-600">
                  {t('storefront.checkout_promo_label')}
                </label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    placeholder={t('storefront.checkout_promo_placeholder')}
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm uppercase"
                    autoComplete="off"
                    disabled={reviewLocked}
                  />
                  <button
                    type="button"
                    disabled={reviewLocked || quoteLoading || !canSubmit}
                    onClick={() => void applyPromo()}
                    className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {quoteLoading ? t('storefront.checkout_promo_applying') : t('storefront.checkout_promo_apply')}
                  </button>
                </div>
                {appliedPromoCode ? (
                  <button
                    type="button"
                    onClick={() => void clearPromo()}
                    className="mt-1.5 text-xs font-semibold text-slate-600 underline"
                    disabled={reviewLocked}
                  >
                    {t('storefront.checkout_promo_clear')}
                  </button>
                ) : null}
                {quoteError ? (
                  <p className="mt-1.5 text-xs text-red-600">{quoteError}</p>
                ) : null}
              </div>

              <label className="mt-3 hidden items-start gap-2 rounded-lg border border-slate-200 bg-white p-2.5 text-[11px] leading-relaxed text-slate-700 sm:flex">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={reviewLocked}
                />
                <span className="min-w-0">
                  {hasPreviouslyAcceptedTerms ? (
                    <>
                      {t('storefront.checkout_terms_reaccept')}{' '}
                      <Link to="/legal" className="font-semibold underline">
                        {t('storefront.checkout_terms_policies_link')}
                      </Link>
                      {t('storefront.checkout_terms_reaccept_suffix')}
                    </>
                  ) : (
                    <>
                      {t('storefront.checkout_terms_first')}{' '}
                      <Link to="/legal" className="font-semibold underline">
                        {t('storefront.checkout_terms_policies_link')}
                      </Link>
                      .
                    </>
                  )}
                  <span className="mt-1 block break-words text-[11px] text-slate-500">
                    {t('storefront.checkout_refund_policy_prefix', {
                      defaultValue: 'By checking this box, you also agree to the',
                    })}{' '}
                    <Link to="/legal#refund-policy" className="font-semibold underline">
                      {t('storefront.checkout_refund_policy_link', {
                        defaultValue: 'Refund Policy',
                      })}
                    </Link>
                    .
                  </span>
                </span>
              </label>

              <button
                type="button"
                disabled={reviewLocked || !canSubmit || !termsAccepted}
                onClick={() => void handleCreateOrder()}
                className="mt-4 hidden w-full rounded-full bg-[#00b853] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60 sm:block"
              >
                {submitting
                  ? t('storefront.placing_order')
                  : t('storefront.checkout_continue_pay')}
              </button>
            </div>

            <aside className="hidden space-y-3 lg:sticky lg:top-4 lg:block">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  {t('storefront.checkout_order_summary')}
                </h2>
                <p className="mt-1 text-xs text-slate-500">{t('storefront.summary')}</p>
              </div>

              <div className="hidden rounded-xl border border-slate-200 bg-white p-3 lg:block">
                <label className="block text-xs font-medium text-slate-600">
                  {t('storefront.checkout_promo_label')}
                </label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    placeholder={t('storefront.checkout_promo_placeholder')}
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm uppercase"
                    autoComplete="off"
                    disabled={reviewLocked}
                  />
                  <button
                    type="button"
                    disabled={reviewLocked || quoteLoading || !canSubmit}
                    onClick={() => void applyPromo()}
                    className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto"
                  >
                    {quoteLoading ? t('storefront.checkout_promo_applying') : t('storefront.checkout_promo_apply')}
                  </button>
                </div>
                {appliedPromoCode ? (
                  <button
                    type="button"
                    onClick={() => void clearPromo()}
                    className="mt-2 text-xs font-semibold text-slate-600 underline"
                    disabled={reviewLocked}
                  >
                    {t('storefront.checkout_promo_clear')}
                  </button>
                ) : null}
                {quoteError ? (
                  <p className="mt-2 text-xs text-red-600">{quoteError}</p>
                ) : null}
                <p className="mt-2 hidden text-xs text-slate-500 xl:block">
                  {t('storefront.checkout_referral_note')}
                </p>
              </div>

              {quote?.marketingPickDiscount?.enabled && step === 'review' ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/90 p-4 text-sm text-emerald-950">
                  {quote.marketingPickDiscount.qualified ? (
                    <>
                      <p className="font-semibold leading-snug">
                        {t('storefront.checkout_pick_discount_qualified', {
                          percent: quote.marketingPickDiscount.percentOffOneLine,
                        })}
                      </p>
                      <p className="mt-3 text-xs font-medium text-emerald-900">
                        {t('storefront.checkout_pick_discount_choose', {
                          percent: quote.marketingPickDiscount.percentOffOneLine,
                        })}
                      </p>
                      <div className="mt-2 space-y-2">
                        {Array.from(new Set(pickProductIdCandidates.map(String))).map((pid) => (
                          <label
                            key={pid}
                            className="flex cursor-pointer items-start gap-2 rounded-lg border border-emerald-200/80 bg-white/95 p-2.5"
                          >
                            <input
                              type="radio"
                              name="pickDiscountProduct"
                              className="mt-1"
                              checked={pickDiscountProductId === pid}
                              onChange={() => setPickDiscountProductId(pid)}
                              disabled={reviewLocked}
                            />
                            <span className="text-slate-800">
                              {productLabels[pid]?.name
                                ?? t('storefront.checkout_pick_discount_product_fallback')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  ) : quote.marketingPickDiscount.amountToQualifyUsd != null &&
                    quote.marketingPickDiscount.amountToQualifyUsd > 0 ? (
                    <p className="leading-snug">
                      {t('storefront.checkout_pick_discount_near', {
                        amount: formatPrice(quote.marketingPickDiscount.amountToQualifyUsd),
                        percent: quote.marketingPickDiscount.percentOffOneLine,
                      })}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="hidden lg:block">
                {quote ? (
                  <SummaryCard
                    summary={quote}
                    showPromoHint
                    displaySubtotal={reviewMerchandiseSubtotalUsd}
                    displayCurrency={displayCurrencyCode}
                  />
                ) : null}
              </div>
            </aside>
          </div>
        )}

        {isCustomer && step === 'pay' && createdOrderId && (
          <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-6">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] lg:items-start lg:gap-x-10 lg:gap-y-8">
              <div className="min-w-0 space-y-4 sm:space-y-6">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    {t('storefront.checkout_pay_title')}
                  </h2>
                  <p className="mt-1 hidden text-sm text-slate-600 sm:block">
                    {t('storefront.checkout_pay_subtitle')}
                  </p>
                  <p className="mt-1 hidden text-xs text-slate-500 sm:block">
                    {t('storefront.checkout_payments_powered_by', {
                      defaultValue: 'Payments powered by',
                    })}{' '}
                    <a
                      href="https://stripe.com"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline"
                    >
                      Stripe
                    </a>
                    .
                  </p>
                </div>

                {payNotice ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    {payNotice}
                    <div className="mt-3">
                      <Link
                        to="/account/orders"
                        className="font-semibold text-[#00b853] underline"
                      >
                        {t('storefront.checkout_view_orders')}
                      </Link>
                    </div>
                  </div>
                ) : null}

                {clientSecret && stripePromise ? (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      locale: stripeLocale,
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#00b853',
                          borderRadius: '12px',
                        },
                      },
                    }}
                  >
                    <CheckoutPaymentForm
                      onSuccess={handlePaySuccess}
                      totalDueFormatted={
                        orderPaymentSummary?.isPreorderOrder
                          ? formatMajorInCurrency(
                              Number(orderPaymentSummary.preorderDepositUsd ?? 0),
                              displayCurrencyCode,
                            )
                          : payStepDisplayTotalMajor != null
                          ? formatMajorInCurrency(
                            Number(payStepDisplayTotalMajor),
                            displayCurrencyCode,
                          )
                          : undefined
                      }
                      estimatedDeliveryDays={
                        orderPaymentSummary?.estimatedDeliveryDays != null
                          ? Number(orderPaymentSummary.estimatedDeliveryDays)
                          : null
                      }
                    />
                  </Elements>
                ) : null}

                {!payNotice && !clientSecret ? (
                  <p className="text-sm text-slate-600">{t('common.loading')}</p>
                ) : null}

                <button
                  type="button"
                  className="hidden text-sm font-semibold text-slate-600 underline sm:inline-block"
                  onClick={() => navigate('/account/orders')}
                >
                  {t('storefront.checkout_pay_later')}
                </button>
              </div>

              <aside className="hidden min-w-0 space-y-4 lg:sticky lg:top-6 lg:block lg:self-start">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t('storefront.checkout_order_reference_label')}
                  </p>
                  <p className="mt-2 break-all font-mono text-[13px] leading-snug text-slate-900">
                    {createdOrderId ? formatOrderReference(createdOrderId) : ''}
                  </p>
                </div>
                {createdPreorderOrderId ? (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-xs text-indigo-900">
                    This checkout created a separate pre-order reference: {formatOrderReference(createdPreorderOrderId)}.
                    Deposit and remaining balance rules are managed on that order.
                  </div>
                ) : null}
                {orderPaymentSummary ? (
                  <SummaryCard
                    summary={orderPaymentSummary}
                    variant="breakdown"
                    displaySubtotal={reviewMerchandiseSubtotalUsd}
                    displayCurrency={displayCurrencyCode}
                  />
                ) : null}
              </aside>
            </div>
          </div>
        )}
        {isCustomer && step === 'review' ? (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2 backdrop-blur sm:hidden">
            <div className="mx-auto max-w-5xl">
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0 text-xs text-slate-600">
                  <div className="font-medium text-slate-700">
                    {t('storefront.subtotal')}: <span className="font-semibold">{reviewSubtotalDisplay}</span>
                  </div>
                  <div>
                    {t('storefront.checkout_delivery_fee')}: <span className="font-semibold">{reviewDeliveryDisplay}</span>
                    {(reviewDiscountMajor ?? 0) > 0 ? (
                      <span> • {t('storefront.checkout_discount')}: <span className="font-semibold">-{reviewDiscountDisplay}</span></span>
                    ) : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t('storefront.checkout_total_due')}
                  </div>
                  <div className="text-xl font-bold text-slate-900">{reviewTotalDisplay}</div>
                </div>
              </div>
              <label className="mb-2 flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] leading-relaxed text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={reviewLocked}
                />
                <span className="min-w-0">
                  <span>
                    {t('storefront.checkout_terms_first')}{' '}
                    <Link to="/legal" className="font-semibold underline">
                      {t('storefront.checkout_terms_policies_link')}
                    </Link>
                  </span>
                  <span className="ml-1 inline-flex align-middle">
                    <span className="group relative inline-flex">
                      <CircleHelp className="h-3.5 w-3.5 text-slate-400" />
                      <span className="pointer-events-none absolute right-0 top-5 z-20 hidden w-56 rounded-md bg-slate-900 px-2 py-1 text-[10px] text-white shadow-lg group-hover:block">
                        {t('storefront.checkout_security_note')} {t('storefront.checkout_note')}
                      </span>
                    </span>
                  </span>
                </span>
              </label>
              <button
                type="button"
                disabled={reviewLocked || !canSubmit || !termsAccepted}
                onClick={() => void handleCreateOrder()}
                className="w-full rounded-full bg-[#00b853] px-6 py-3 text-sm font-semibold text-white hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? t('storefront.placing_order')
                  : t('storefront.checkout_continue_pay')}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </StorefrontLayout>
  );
}
